// Feedback about Majlis (2026-10-27; Amine: "Feedback which take a screenshots automatically where they are"): one
// report at a time, from anywhere: the topbar, the command palette, the welcome. Opening it captures the visible page
// first (in this browser, nothing sent), then shows the dialog (FeedbackDialog, mounted once in App.vue) to review it.
// "Replace screenshot" hides the dialog and keeps the draft: the person scrolls to what they want to show and takes it.
import { reactive, ref, shallowRef } from "vue";
import { captureView } from "@/features/feedback/capture";
import type { CapabilityKind, FeedbackKind } from "@/api/productFeedback";

const open = ref(false);
const capturing = ref(false);
const retaking = ref(false);
const shot = shallowRef<HTMLCanvasElement | null>(null);   // memory only: dropped when the report is sent or dropped
const captureFailed = ref(false);
const where = reactive({ screen: "", route: "" });
// a request (2026-10-28): what kind of thing is missing and the product it names
const draft = reactive({ kind: "bug" as FeedbackKind, what: "", instead: "", key: "", capability: "connection" as CapabilityKind, product: "" });

function newKey(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return `fb-${[...bytes].map((b) => b.toString(16).padStart(2, "0")).join("")}`;
}

async function capture(): Promise<void> {
  capturing.value = true;
  try {
    shot.value = await captureView();
    captureFailed.value = false;
  } catch {
    shot.value = null;
    captureFailed.value = true;   // the report still goes: in words, without a picture
  } finally {
    capturing.value = false;
  }
}

/** `preset`: a report started for something in particular, e.g. Connectors' "Ask for it": a request for a connection. */
export async function startFeedback(screen: string, route: string,
                                    preset?: { kind?: FeedbackKind; capability?: CapabilityKind; product?: string }): Promise<void> {
  if (capturing.value || open.value || retaking.value) return;
  Object.assign(where, { screen, route });
  Object.assign(draft, { kind: preset?.kind ?? "bug", what: "", instead: "", key: newKey(),   // one key per report: a resend is the same report
                         capability: preset?.capability ?? "connection", product: preset?.product ?? "" });
  await capture();
  open.value = true;
}

/** Hides the dialog, keeping the draft, until the person takes the screenshot again or keeps the one they had. */
export function retake(): void {
  open.value = false;
  retaking.value = true;
}

export async function takeAgain(): Promise<void> {
  retaking.value = false;
  await new Promise((r) => requestAnimationFrame(() => r(null)));   // the bar leaves the page first
  await capture();
  open.value = true;
}

export function keepShot(): void {
  retaking.value = false;
  open.value = true;
}

/** Drops the report: its draft and screenshot leave memory. */
export function closeFeedback(): void {
  open.value = false;
  retaking.value = false;
  shot.value = null;
  Object.assign(draft, { what: "", instead: "", key: "", product: "" });
}

export function useFeedback() {
  return { open, capturing, retaking, shot, captureFailed, where, draft, start: startFeedback, retake, takeAgain, keepShot,
           close: closeFeedback };
}
